import React from 'react'
// libs
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useMutation } from '@apollo/client/react'
import { formValueSelector, InjectedFormProps, reduxForm } from 'redux-form'
// components
import { Button, BUTTON_LAYOUTS } from 'components/UI'
import { ReduxSwitch } from 'components/FormElements/ReduxForm'
import InputLabel from 'components/FormElements/components/InputLabel'
// views
import { ButtonWrapper, Header, Paper } from '../../views'
// view components
import SingleReviewForm from './components/SingleReviewForm'
import RepeatingReviewForm from './components/RepeatingReviewForm'
// constants
import { Forms, Modals } from 'constants/index'
import { REVIEW_STATUSES } from 'constants/review'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// themes
import { CloseIcon } from 'components/UI/Icons'
// graphql
import { SCHEDULED_REVIEW_DELETE_MUTATION } from 'graphql/reviews/mutations'
// hooks
import { useCurrentUser, useModalData, useShowModal } from 'hooks/index'
// utils
import moment from 'moment'
import { safeGetOr } from 'utils/fp'
import { map, pipe } from 'lodash/fp'
import { handleError } from 'utils/errors/handlers'
import { get, isEmpty, isFunction } from 'lodash'
import { getRefetchQueriesForReviews } from 'utils/reviews'

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  > div {
    width: fit-content;
  }
`

interface IIncomingProps {
  entity: any
  review: any
  hideModal: () => void
  handleSetIsReviewNow: (value: boolean) => void
}

const selector = formValueSelector(Forms.SCHEDULE_REVIEW_FORM)

const ScheduleReviewForm: React.FunctionComponent<InjectedFormProps<any, IIncomingProps> & IIncomingProps> = ({
  reset,
  review,
  entity,
  hideModal,
  submitting,
  handleSubmit,
  handleSetIsReviewNow
}) => {
  const [deleteScheduledReview] = useMutation(SCHEDULED_REVIEW_DELETE_MUTATION)

  const { treeQueryRefetch } = React.useContext(PlanEditPageContext)

  // redux
  const currentUser = useCurrentUser()

  const {
    modalData: { canDelete, isEdit }
  } = useModalData({ modalType: Modals.SCHEDULE_REVIEW_MODAL })

  const { showModal: showConfirmDeleteModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_SCHEDULED_REVIEW })

  const { isRepeatingReview, singleReviewReviewers, startPeriod, repeatingReviewReviewers } = useSelector((state) => ({
    isRepeatingReview: selector(state, 'repeating'),
    singleReviewReviewers: selector(state, 'singleReviewReviewers'),
    startPeriod: selector(state, 'startPeriod'),
    repeatingReviewReviewers: selector(state, 'repeatingReviewReviewers')
  }))

  // constants
  const reviewers = (!isRepeatingReview && singleReviewReviewers) || (isRepeatingReview && repeatingReviewReviewers)

  const chosenReviewersIds = React.useMemo(() => {
    if (reviewers) {
      return reviewers.map((item) => get(item, 'reviewer.value')).filter((reviewer) => reviewer)
    }
    return []
  }, [reviewers])

  const myPositionIds = pipe<any, any, any>(safeGetOr([], 'positions'), map('id'))(currentUser)

  const shouldShowReviewNowButton =
    reviewers &&
    reviewers.some((data) => {
      const reviewerId = get(data, 'reviewer.value')
      return reviewerId === currentUser.id || myPositionIds.includes(reviewerId)
    })

  const shouldDisableSaveButton = isEmpty(get(reviewers, '[0]'))

  const reviewStatus = get(review, 'status', REVIEW_STATUSES.NOT_STARTED)

  // methods
  const handleDeleteReview = () => {
    const refetchQueries = getRefetchQueriesForReviews(entity)

    showConfirmDeleteModal({
      width: 'sm',
      handleConfirm: async () => {
        try {
          if (isFunction(treeQueryRefetch)) {
            await treeQueryRefetch()
          }

          await deleteScheduledReview({
            variables: {
              id: review.scheduledReview.id
            },
            refetchQueries
          })

          hideModal()
        } catch (errors) {
          handleError(errors, 'ScheduleReviewForm.handleDeleteReview')
        }
      }
    })
  }

  React.useEffect(() => () => handleSetIsReviewNow(false), [])

  return (
    <Paper component={'form'} onSubmit={handleSubmit}>
      <Header>
        <span>Schedule Review</span>
        <CloseIcon right={0} onClick={hideModal} />
      </Header>
      {!isEdit && (
        <SwitchContainer>
          <InputLabel> Schedule repeating review </InputLabel>
          <ReduxSwitch name={'repeating'} justifyContent={'flex-end'} onClick={reset} />
        </SwitchContainer>
      )}
      {!isRepeatingReview ? (
        <SingleReviewForm chosenReviewersIds={chosenReviewersIds} />
      ) : (
        <RepeatingReviewForm chosenReviewersIds={chosenReviewersIds} />
      )}
      <ButtonWrapper container>
        {isEdit ? (
          <Button
            shadowLess
            type={'submit'}
            isLoading={submitting}
            layout={BUTTON_LAYOUTS.PURPLE}
            disabled={shouldDisableSaveButton}>
            Save
          </Button>
        ) : (
          <>
            <Button type={'submit'} layout={BUTTON_LAYOUTS.PURPLE} shadowLess disabled={shouldDisableSaveButton}>
              Schedule
            </Button>
          </>
        )}

        {shouldShowReviewNowButton && (
          <Button
            type={'submit'}
            layout={BUTTON_LAYOUTS.PURPLE_INVERTED}
            shadowLess
            onClick={() => handleSetIsReviewNow(true)}
            disabled={
              (review && !moment().isSameOrAfter(review.startPeriod, 'day')) ||
              !moment().isSameOrAfter(startPeriod, 'day')
            }>
            Review Now
          </Button>
        )}

        {isEdit && canDelete && reviewStatus === REVIEW_STATUSES.NOT_STARTED && (
          <Button type={'button'} layout={BUTTON_LAYOUTS.PURPLE_INVERTED} shadowLess onClick={handleDeleteReview}>
            Delete
          </Button>
        )}
      </ButtonWrapper>
    </Paper>
  )
}

export default reduxForm<any, IIncomingProps>({ form: Forms.SCHEDULE_REVIEW_FORM })(ScheduleReviewForm)
